/**
 * IAM auth seam — Phase 2 implementation (replaces the Phase 1 stub).
 *
 * The OIDC Authorization Code + PKCE flow against Medicus IAM (ADR-0021,
 * ADR-0041 §"Auth flow") is implemented across:
 *
 * - `auth/pkce.ts` — RFC 7636 verifier/challenge/state generation.
 * - `auth/tokens.ts` — JWT decode + sessionStorage helpers for the verifier.
 * - `auth/state.ts` — pure state machine (unauthenticated → authenticating
 *   → authenticated → refreshing → expired).
 * - `auth/flow.ts` — `buildLoginRedirect`, `handleCallback`, `attemptRefresh`,
 *   `buildLogoutRedirect`. The token exchange is proxied through the backend
 *   (`POST /api/v1/auth/exchange`) so the SPA never holds the IAM client
 *   secret (per readiness Q-2 IAM contract).
 * - `auth/provider.tsx` — React provider wiring silent-refresh + idle
 *   timeout + the snapshot-based hook.
 *
 * Consumers of `useIAMAuth()` receive the locked surface from the chip plan
 * (`isReady`, `isAuthenticated`, `permissions`, `signIn`, `signOut`).
 */

import { useAuthSnapshot, signIn, signOut } from "@/auth/provider";

export class IAMNotConfiguredError extends Error {
  override name = "IAMNotConfiguredError";
}

/**
 * Shape the `<AuthProvider>` consumes from `src/config.ts`. Locked here so
 * future provider-config refactors flow through a single type.
 */
export interface IAMConfig {
  /** OIDC issuer URL — Medicus IAM tenant root. */
  authority: string;
  /** Public OIDC client_id registered with IAM for this SPA. */
  clientId: string;
  /** Browser callback URL (post-IAM redirect). */
  redirectUri: string;
  /** Space-separated scope string; must include `openid`. */
  scope: string;
  /** Post-logout redirect URL. */
  postLogoutRedirectUri: string;
}

export interface IAMAuthContext {
  /** True once provider has mounted and any in-flight refresh has resolved. */
  isReady: boolean;
  /** True iff we hold a non-expired access token. */
  isAuthenticated: boolean;
  /** Decoded user info (sub, email, name) — populated post-token-exchange. */
  user: { sub: string; email?: string; name?: string } | null;
  /** Permission claims from the JWT (ADR-0026 user-RBAC layer). */
  permissions: ReadonlyArray<string>;
  /** ADR-0022 role, informational only. It gates nothing: real IAM does not set it
   * for an invited portal user, so the portal gates on the resolved permission. */
  role: string | null;
  /** The `roles` list real IAM sends since iam-service !340. Informational, gates
   * nothing, `[]` when absent. */
  roles: ReadonlyArray<string>;
  /** The caller's tenant. Never sent as a request parameter; the backend
   * reads the same claim off the token. `null` for Medicus staff. */
  organizationId: string | null;
  /** OAuth2 scopes from the access token (ADR-0026 client-capability layer). */
  scopes: ReadonlyArray<string>;
  /** Trigger OIDC redirect to IAM. `next` lands the user post-callback. */
  signIn: (next?: string) => Promise<void>;
  /** Clear in-memory state + redirect to IAM end-session. */
  signOut: () => Promise<void>;
}

export function useIAMAuth(): IAMAuthContext {
  const snapshot = useAuthSnapshot();
  const claims = snapshot.claims;
  return {
    // The state machine starts in `unauthenticated` synchronously, so the
    // provider is "ready" as soon as it has rendered once. The Phase 1
    // stub's `isReady` was always false; Phase 2 flips it true by default.
    isReady: snapshot.status !== "authenticating" && snapshot.status !== "refreshing",
    isAuthenticated: snapshot.status === "authenticated",
    user:
      claims === null
        ? null
        : (() => {
            const u: { sub: string; email?: string; name?: string } = {
              sub: claims.sub,
            };
            if (claims.email !== undefined) u.email = claims.email;
            if (claims.name !== undefined) u.name = claims.name;
            return u;
          })(),
    permissions: claims?.permissions ?? [],
    scopes: claims?.scopes ?? [],
    role: claims?.role ?? null,
    roles: claims?.roles ?? [],
    organizationId: claims?.organization_unit_id ?? null,
    signIn,
    signOut,
  };
}
